<template>
  <Dropdown
    :visible="dropdownVisible"
    trigger="custom"
    style="width: 100%"
    class="scroll-select"
    @on-clickoutside="dropdownVisible = false"
  >
    <Select
      :value="selectValue"
      :multiple="multiple"
      filterable
      clearable
      @on-query-change="onFilterTextChange"
      @on-change="changeSelectValue"
      @click.native="dropdownVisible = !dropdownVisible"
      @on-clickoutside="onSelectClickOutside"
    >
      <!-- Have to use dummy options for value to show up in select input bar -->
      <Option
        v-for="option in selectedOptions"
        :key="`selected.${option.value}`"
        :value="option.value"
      >
        {{ option.label }}
      </Option>
    </Select>
    <!--
      Need to resort to using external dropdown menu for scroll select as filtering does
      not work well with iview's Select component when used with Scroll
    -->
    <DropdownMenu
      slot="list"
      class="dropdown-menu"
    >
      <Scroll
        v-if="selectOptions.length"
        :on-reach-bottom="onScrollBottom"
        :height="dropdownHeight"
        class="dropdown-scroll"
        @scroll="event => {
          event.preventDefault()
        }"
      >
        <DropdownItem
          v-for="option in selectOptions"
          :key="option.value"
          :name="option.value"
          :class="{ 'scroll-select-dropdown-item': true, 'selected': isOptionSelected(option.value) }"
          @click.native="onDropDownSelect(option.value)"
        >
          {{ option.label }}
        </DropdownItem>
      </Scroll>
      <DropdownItem
        v-else
        class="no-item-found"
      >
        No matching data
      </DropdownItem>
    </DropdownMenu>
  </Dropdown>
</template>

<script>
export default {
  model: {
    prop: 'value',
    event: 'change'
  },

  props: {
    value: {
      type: [String, Array],
      default: ''
    },
    options: {
      type: Array,
      required: true
    },
    initialLimit: {
      type: Number,
      default: 50
    },
    increment: {
      type: Number,
      default: 50
    },
    multiple: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      limit: this.initialLimit,
      loading: false,
      filterText: '',
      dropdownVisible: false
    }
  },

  computed: {
    selectValue () {
      return this.value
    },

    selectOptions () {
      let options = this.options

      if (this.filterText) {
        options = options.filter(option => option.label.toLowerCase().indexOf(this.filterText.toLowerCase()) > -1)
      }

      return options.slice(0, this.limit)
    },

    selectedOptions () {
      return this.options.filter(option => {
        if (this.multiple) {
          return this.selectValue.includes(option.value)
        } else {
          return this.selectValue === option.value
        }
      })
    },

    dropdownHeight () {
      // 28 is the height of  drop down item
      // 4 is the padding added to the drop down list
      const height = (this.selectOptions.length * 28) + 4

      return Math.min(height, 200)
    }
  },

  methods: {
    /**
     * Evoked when scroll reaches the bottom
     */
    onScrollBottom () {
      return new Promise(resolve => {
        if (this.limit < this.options.length) {
          this.limit += this.increment
        }

        resolve()
      })
    },

    /**
     * Evoked when user clicks outside of select component
     * This event handler needs to be used because the select click outside event
     * prevents drop down item from being selected when drop down is initially opened
     * @param {Event} event
     */
    onSelectClickOutside (event) {
      if (event.target.__vue__ && event.target.className.includes('scroll-select-dropdown-item')) {
        this.onDropDownSelect(event.target.__vue__.name)
      } else {
        this.dropdownVisible = false
      }
    },

    /**
     * Evoked when a drop down item is selected
     * @param {String} value
     */
    onDropDownSelect (value) {
      if (this.multiple) {
        const valueArr = Array.isArray(this.selectValue) ? this.selectValue : []
        const valueIndex = valueArr.findIndex(val => val === value)

        if (valueIndex < 0) {
          valueArr.push(value)
        } else {
          valueArr.splice(valueIndex, 1)
        }

        this.changeSelectValue(valueArr)
      } else {
        this.changeSelectValue(value)
      }
    },

    /**
     * Evoked when the filter text is changed
     * @param {String} filterText
     */
    onFilterTextChange (filterText) {
      this.filterText = filterText
    },

    /**
     * Evoked when select value has been changed
     * @param {String/Array} value
     */
    changeSelectValue (value) {
      this.$emit('change', value)
      this.$emit('onChange', value)
    },

    /**
     * Checks to see if an option is selected
     * @param {String} value
     * @return {Boolean}
     */
    isOptionSelected (value) {
      return this.selectedOptions.some(option => option.value === value)
    }
  }
}
</script>

<style scoped>
  .dropdown-scroll >>> .ivu-scroll-loader {
    height: 0;
  }

  .dropdown-scroll >>> .ivu-scroll-container {
    max-height: 200px;
  }

  .scroll-select >>> .ivu-select .ivu-select-dropdown {
    display: none;
  }

  .scroll-select .dropdown-menu {
    max-height: 200px;
  }

  .scroll-select-dropdown-item.selected {
    position: relative;
    color: var(--color-primary);
  }

  .scroll-select .no-item-found {
    text-align: center;
    color: #c5c8ce;
  }

  .scroll-select-dropdown-item.selected::after {
    display: inline-block;
    font-family: Ionicons;
    font-style: normal;
    font-weight: 400;
    font-variant: normal;
    text-transform: none;
    text-rendering: optimizeLegibility;
    line-height: 1;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    vertical-align: -.125em;
    text-align: center;
    font-size: 24px;
    content: '\F171';
    color: var(--color-primary);
    position: absolute;
    top: 2px;
    right: 8px;
  }
</style>
